import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { watchOrdiniLidoOggi, updateOrdineLidoStato } from '../services/firebase';
import Topbar from '../components/layout/Topbar';
import { Button, Spinner } from '../components/ui';

// Stati in cui può trovarsi un ordine — mappa anche label, colore e prossima
// transizione naturale ("cosa cliccherà il gestore dopo per progredire").
// Annullato è esterno al flusso felice ed è sempre disponibile.
const STATI = {
    ricevuto: { label: 'Ricevuto', color: '#2b9fe9', next: 'in_preparazione', cta: 'Prendi in carico' },
    in_preparazione: { label: 'In preparazione', color: '#e6a23c', next: 'consegnato', cta: 'Pronto da consegnare' },
    consegnato: { label: 'Consegnato', color: '#34c77b', next: 'pagato', cta: 'Saldato in cassa' },
    pagato: { label: 'Pagato', color: '#5d6675', next: null, cta: null },
    annullato: { label: 'Annullato', color: '#ec6a6a', next: null, cta: null },
};

const FILTRI = [
    { id: 'attivi', label: 'Attivi', match: o => o.stato !== 'pagato' && o.stato !== 'annullato' },
    { id: 'tutti', label: 'Tutti', match: () => true },
    { id: 'completati', label: 'Completati', match: o => o.stato === 'pagato' || o.stato === 'annullato' },
];

const PAGE_CSS = `
.ord-page-wrap { padding: 28px 32px 60px; max-width: 1240px; margin: 0 auto; }
.ord-page-head { margin-bottom: 22px; }
.ord-page-head h1 { font-size: 26px; font-weight: 700; letter-spacing: -0.4px; color: var(--text); }
.ord-page-head p { color: var(--text-muted); font-size: 14.5px; margin-top: 6px; max-width: 680px; line-height: 1.55; }

.ord-summary {
  display: flex; align-items: center; gap: 18px;
  background: var(--surface); border: 1px solid var(--border);
  border-radius: 14px; padding: 18px 22px; margin-bottom: 22px;
  position: relative; overflow: hidden;
}
.ord-summary::before {
  content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: 3px;
  background: #2b9fe9;
}
.ord-summary__title { font-weight: 700; font-size: 15.5px; color: var(--text); }
.ord-summary__sub { color: var(--text-muted); font-size: 13.5px; margin-top: 3px; }
.ord-summary__text { flex: 1; }
.ord-summary__meta {
  display: flex; align-items: center; gap: 6px; color: var(--text-muted); font-size: 12.5px;
  padding-right: 22px; border-right: 1px solid var(--border); margin-right: 4px;
}
.ord-summary__meta:last-of-type { border: none; padding-right: 0; margin-right: 0; }
.ord-summary__meta b { color: var(--text); font-weight: 700; }

.ord-filters { display: flex; gap: 8px; margin-bottom: 18px; flex-wrap: wrap; }
.ord-chip {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 9px 15px; border-radius: 10px;
  background: var(--surface); border: 1px solid var(--border);
  color: var(--text-muted); font-size: 13.5px; font-weight: 600; cursor: pointer;
  font-family: inherit; transition: .15s;
}
.ord-chip:hover { color: var(--text); border-color: var(--border-mid); }
.ord-chip__n { font-size: 11.5px; font-weight: 700; background: var(--border); color: var(--text-muted); padding: 1px 7px; border-radius: 20px; line-height: 1.5; }
.ord-chip--active { background: rgba(43,159,233,.14); border-color: rgba(43,159,233,.45); color: #9ed4f7; }
.ord-chip--active .ord-chip__n { background: rgba(43,159,233,.25); color: #bfe2fa; }

.ord-list { display: grid; grid-template-columns: 1fr; gap: 12px; }

.ord-card {
  background: var(--surface); border: 1px solid var(--border);
  border-radius: 14px; padding: 18px 22px;
  display: grid; grid-template-columns: 1fr auto; gap: 18px; align-items: flex-start;
  position: relative; overflow: hidden;
  transition: border-color .15s;
}
.ord-card[data-stato="ricevuto"] { border-color: rgba(43,159,233,.35); }
.ord-card__head { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; flex-wrap: wrap; }
.ord-card__ombrellone {
  background: rgba(43,159,233,.14); color: #2b9fe9;
  padding: 4px 10px; border-radius: 8px; font-weight: 700; font-size: 14px;
}
.ord-card__guest { font-size: 13.5px; color: var(--text-muted); }
.ord-card__time { font-size: 12px; color: var(--text-muted); margin-left: auto; font-variant-numeric: tabular-nums; }

.ord-card__items { font-size: 14px; color: var(--text); line-height: 1.55; }
.ord-card__items .it { display: flex; gap: 8px; padding: 3px 0; }
.ord-card__items .it b { font-weight: 700; color: #2b9fe9; min-width: 28px; }
.ord-card__note {
  margin-top: 8px; padding: 8px 12px; background: rgba(230,162,60,.08);
  border: 1px solid rgba(230,162,60,.25); border-radius: 8px;
  font-size: 12.5px; color: #e6a23c; line-height: 1.5;
}
.ord-card__bottom {
  display: flex; align-items: center; justify-content: space-between;
  gap: 14px; margin-top: 14px; padding-top: 12px; border-top: 1px solid var(--border);
}
.ord-card__totale { font-size: 17px; font-weight: 700; color: var(--text); font-variant-numeric: tabular-nums; }

.ord-card__side { display: flex; flex-direction: column; gap: 8px; align-items: stretch; min-width: 200px; }

.ord-pill {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 5px 11px; border-radius: 20px;
  font-size: 12.5px; font-weight: 700; white-space: nowrap;
  align-self: flex-start;
}
.ord-pill::before { content: ""; width: 7px; height: 7px; border-radius: 50%; }

.ord-empty {
  padding: 80px 28px; text-align: center;
  background: var(--surface); border: 1px solid var(--border); border-radius: 14px;
}
.ord-empty__icon { font-size: 38px; margin-bottom: 14px; }
.ord-empty__title { font-size: 16px; font-weight: 700; color: var(--text); margin-bottom: 4px; }
.ord-empty__sub { font-size: 13.5px; color: var(--text-muted); }

@media (max-width: 720px) {
  .ord-card { grid-template-columns: 1fr; }
  .ord-card__side { min-width: 0; }
}
`;

function formatHHMM(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function OrdineCard({ ordine, onAdvance, onCancel, busy }) {
    const cfg = STATI[ordine.stato] ?? STATI.ricevuto;

    return (
        <div className="ord-card" data-stato={ordine.stato}>
            <div>
                <div className="ord-card__head">
                    <span className="ord-card__ombrellone">🏖 {ordine.ombrelloneNumero || '—'}</span>
                    <span className="ord-card__guest">{ordine.guestName ?? 'Ospite'}</span>
                    <span className="ord-card__time">{formatHHMM(ordine.createdAt)}</span>
                </div>
                <div className="ord-card__items">
                    {(ordine.items ?? []).map((it, idx) => (
                        <div className="it" key={idx}>
                            <b>{it.quantita}×</b>
                            <span>{it.nome}</span>
                        </div>
                    ))}
                </div>
                {ordine.note && (
                    <div className="ord-card__note">📝 {ordine.note}</div>
                )}
                <div className="ord-card__bottom">
                    <span className="ord-card__totale">€ {Number(ordine.totale ?? 0).toFixed(2)}</span>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                        {ordine.pagamento?.metodo === 'cash' ? 'Da pagare in cassa' : ''}
                    </span>
                </div>
            </div>

            <div className="ord-card__side">
                <span className="ord-pill" style={{ background: `${cfg.color}22`, color: cfg.color }}>
                    <span style={{ background: cfg.color, width: 7, height: 7, borderRadius: 99, display: 'inline-block' }} />
                    {cfg.label}
                </span>
                {cfg.next && (
                    <Button
                        variant="primary"
                        onClick={() => onAdvance(ordine.id, cfg.next)}
                        disabled={busy}
                    >
                        {busy ? <Spinner size={14} /> : `→ ${cfg.cta}`}
                    </Button>
                )}
                {ordine.stato !== 'pagato' && ordine.stato !== 'annullato' && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onCancel(ordine.id)}
                        disabled={busy}
                    >
                        Annulla
                    </Button>
                )}
            </div>
        </div>
    );
}

export default function OrdiniLido() {
    const { admin } = useAuth();
    const [ordini, setOrdini] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filtro, setFiltro] = useState('attivi');
    const [busyId, setBusyId] = useState(null);

    useEffect(() => {
        if (!admin?.beachCode) return;
        const unsub = watchOrdiniLidoOggi(admin.beachCode, items => {
            setOrdini(items);
            setLoading(false);
        });
        return () => unsub();
    }, [admin?.beachCode]);

    // Conteggi per i chip e per la summary
    const counts = useMemo(() => ({
        attivi: ordini.filter(FILTRI[0].match).length,
        tutti: ordini.length,
        completati: ordini.filter(FILTRI[2].match).length,
    }), [ordini]);

    const fattoOggi = useMemo(() => {
        return ordini
            .filter(o => o.stato === 'pagato')
            .reduce((sum, o) => sum + Number(o.totale ?? 0), 0);
    }, [ordini]);

    const filtered = ordini.filter(FILTRI.find(f => f.id === filtro).match);

    const handleAdvance = async (id, next) => {
        setBusyId(id);
        try {
            await updateOrdineLidoStato(admin.beachCode, id, next);
        } catch (err) {
            alert(`Errore aggiornamento: ${err.message}`);
        } finally {
            setBusyId(null);
        }
    };

    const handleCancel = async (id) => {
        if (!window.confirm('Annullare questo ordine? Il cliente vedrà lo stato cambiato nell\'app.')) return;
        setBusyId(id);
        try {
            await updateOrdineLidoStato(admin.beachCode, id, 'annullato');
        } catch (err) {
            alert(`Errore: ${err.message}`);
        } finally {
            setBusyId(null);
        }
    };

    return (
        <>
            <style>{PAGE_CSS}</style>
            <Topbar />
            <div className="ord-page-wrap">
                <div className="ord-page-head">
                    <h1>Ordini ombrellone</h1>
                    <p>Ordini dei clienti in tempo reale. Tocca il bottone di avanzamento per progredire lo stato — l'app del cliente si aggiorna automaticamente.</p>
                </div>

                {/* Summary banner */}
                <div className="ord-summary">
                    <div className="ord-summary__text">
                        <div className="ord-summary__title">Oggi</div>
                        <div className="ord-summary__sub">Aggiornamento in tempo reale, niente refresh.</div>
                    </div>
                    <div className="ord-summary__meta"><span><b>{counts.attivi}</b>{' '}da gestire</span></div>
                    <div className="ord-summary__meta"><span><b>{counts.completati}</b>{' '}chiusi</span></div>
                    <div className="ord-summary__meta"><span><b>€ {fattoOggi.toFixed(2)}</b>{' '}fatturato</span></div>
                </div>

                {/* Filtri */}
                <div className="ord-filters">
                    {FILTRI.map(f => (
                        <button
                            key={f.id}
                            type="button"
                            className={`ord-chip ${filtro === f.id ? 'ord-chip--active' : ''}`}
                            onClick={() => setFiltro(f.id)}
                        >
                            {f.label} <span className="ord-chip__n">{counts[f.id]}</span>
                        </button>
                    ))}
                </div>

                {/* Lista */}
                {loading ? (
                    <div style={{ textAlign: 'center', padding: 60 }}><Spinner /></div>
                ) : filtered.length === 0 ? (
                    <div className="ord-empty">
                        <div className="ord-empty__icon">{filtro === 'attivi' ? '🌊' : '📭'}</div>
                        <div className="ord-empty__title">
                            {filtro === 'attivi' ? 'Nessun ordine da gestire' : 'Nessun ordine'}
                        </div>
                        <div className="ord-empty__sub">
                            {filtro === 'attivi' ? 'Quando arriva un nuovo ordine apparirà qui in tempo reale.' : 'Nessun ordine in questa vista.'}
                        </div>
                    </div>
                ) : (
                    <div className="ord-list">
                        {filtered.map(o => (
                            <OrdineCard
                                key={o.id}
                                ordine={o}
                                onAdvance={handleAdvance}
                                onCancel={handleCancel}
                                busy={busyId === o.id}
                            />
                        ))}
                    </div>
                )}
            </div>
        </>
    );
}
